import OverflowRow from './OverflowRow';

type Tone = 'neutral' | 'danger' | 'warning' | 'success';

interface Option<K extends string> {
  key: K;
  label: string;
  count?: number;
  tone?: Tone;
  /** Leading status dot (design `.tab` dot). */
  dot?: Tone;
}

interface Props<K extends string> {
  options: Option<K>[];
  value: K;
  onChange: (k: K) => void;
  label: string;
  /** Single row with prev/next arrows when the options overflow. */
  scroll?: boolean;
  /** Render counts as the design's small rounded badge instead of plain mono text. */
  badge?: boolean;
  /** Expose as a tablist (role=tab, aria-selected) instead of pressed buttons. */
  tabs?: boolean;
  /** Design `.pills` size: 36px tall, weight 500 (filter rows). */
  dense?: boolean;
  /** Design `.sch` chip size: 38px tall, 13.5px, weight 500 (school filter). */
  chip?: boolean;
  /**
   * Explicit pill height for the artboard variants the presets miss: 34 (CaseDetail, 12.5px),
   * 36 (ParentDigest / Paper `.tab`), 38 (SchoolTasks). Overrides `dense`'s height.
   */
  height?: 34 | 36 | 38 | 40;
  /** Explicit label weight (`dense` and `chip` default to medium, the rest to semibold). */
  weight?: 'medium' | 'semibold';
}

const HEIGHT = { 34: 'min-h-[34px] px-3 text-[12.5px]', 36: 'min-h-[36px] px-[13px] text-[13px]', 38: 'min-h-[38px] px-3.5 text-[13px]', 40: 'min-h-[40px] px-3.5 text-[13px]' } as const;

const DOT_TONE: Record<Tone, string> = {
  neutral: 'bg-track',
  danger: 'bg-danger',
  warning: 'bg-warning',
  success: 'bg-success',
};

const COUNT_TONE: Record<Tone, string> = {
  neutral: 'text-muted',
  danger: 'text-danger-ink',
  warning: 'text-warning-ink',
  success: 'text-success',
};

export default function PillTabs<K extends string>({ options, value, onChange, label, scroll = false, badge = false, tabs = false, dense = false, chip = false, height, weight }: Props<K>) {
  if (options.length === 0) return null;
  const preset = chip ? 'min-h-[38px] px-3 text-[13.5px]' : `${dense ? 'min-h-[36px]' : 'min-h-[40px]'} px-3.5 text-[13px]`;
  const sizing = height ? HEIGHT[height] : preset;
  const fontWeight = (weight ?? (chip || dense ? 'medium' : 'semibold')) === 'medium' ? 'font-medium' : 'font-semibold';
  // Design `.pills` (dense) rows and their inner gap are 6px; the other pills use 8px.
  const gap = dense ? 'gap-1.5' : 'gap-2';
  const buttons = (
    <>
      {options.map((o) => {
        const active = o.key === value;
        return (
          <button key={o.key} type="button" {...(tabs ? { role: 'tab', 'aria-selected': active } : { 'aria-pressed': active })} onClick={() => onChange(o.key)}
            className={`inline-flex ${sizing} ${fontWeight} items-center ${gap} rounded-full border transition-colors ${scroll ? 'shrink-0 whitespace-nowrap ' : ''}${
              active
                ? 'border-navy bg-navy text-white'
                : `border-line-strong bg-surface ${chip ? 'text-ink' : 'text-muted-2'} hover:border-line-hover hover:text-ink`}`}>
            {o.dot && <span aria-hidden="true" data-testid="pill-dot" className={`h-2 w-2 flex-none rounded-full ${DOT_TONE[o.dot]}`} />}
            <span>{o.label}</span>
            {o.count !== undefined && badge && tabs && <span className="sr-only">{`, ${o.count}`}</span>}
            {o.count !== undefined && badge && (
              <span aria-hidden={tabs ? true : undefined} className={`rounded-full px-[7px] py-0.5 font-mono text-[11px] font-semibold ${active ? 'bg-navy-3 text-mist-fixed' : 'bg-ground text-muted-2'}`}>{o.count}</span>
            )}
            {o.count !== undefined && !badge && (
              <span className={`font-mono text-xs ${active ? (chip ? 'text-on-navy' : 'text-accent') : COUNT_TONE[o.tone ?? 'neutral']}`}>{o.count}</span>
            )}
          </button>
        );
      })}
    </>
  );
  if (scroll) {
    return <OverflowRow role={tabs ? 'tablist' : 'group'} label={label} activeKey={value} rowClassName={gap}>{buttons}</OverflowRow>;
  }
  return <div role={tabs ? 'tablist' : 'group'} aria-label={label} className={`flex flex-wrap ${gap}`}>{buttons}</div>;
}
